var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { KompisElement, property } from '@inter-ikea-kompis/web-component';
import { SheetAlignmentEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import { DexfSettingsUtility } from '@inter-ikea-kompis/utilities';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import { ModalFooter as SkapaModalFooter, ModalHeader as SkapaModalHeader, Prompt as SkapaPrompt, Sheet as SkapaSheet } from '@ingka/modal-webc/passive.js';
import ZipInActionEnum from '../../enums/ZipInActionEnum.js';
import ZipInCardStateEnum from '../../enums/ZipInCardStateEnum.js';
import ZipInModalEnum from '../../enums/ZipInModalEnum.js';
import ZipInTracker from '../../trackers/ZipInTracker.js';
import ZipInCard from '../zip-in-card/ZipInCard.js';
/**
 * This component is used where users can enter their postcode.
 *
 * @event modalClose Modal close.
 * @event modalOpen Modal open.
 * @event save Triggered when the save button is clicked.
 * @event forget Triggered when the forget button is clicked.
 *
 * @tracker packages/component-zip-in/src/trackers/ZipInTracker.ts
 */
export default class ZipIn extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service. Required for analytics.
        this.settings = null;
        // Visible modal. Use ZipInModalEnum.
        this.visibleModal = null;
        // Current zip code. Changing this will update the value of the input field.
        this.zipCode = null;
        // Card state. Use ZipInCardStateEnum.
        this.state = ZipInCardStateEnum.default;
        // Where the sheet will open. The values are flipped in RTL, meaning "right" will open to the left and vice versa.
        this.sheetAlignment = SheetAlignmentEnum.right;
        // Used for automatic analytics. If null or not set, analytics is turned off.
        this.analytics = null;
        this.tracker = null;
        this.dexfSettings = null;
        this.sheetElement = null;
        this.cardElement = null;
        this.sheetFooterElement = null;
    }
    render() {
        return null;
    }
    async connectedCallback() {
        this.customElements.define('skapa-sheet', SkapaSheet);
        this.customElements.define('skapa-modal-footer', SkapaModalFooter);
        this.customElements.define('skapa-modal-header', SkapaModalHeader);
        this.customElements.define('skapa-prompt', SkapaPrompt);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('kompis-zip-in-card', ZipInCard);
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.closeSheet();
        this.disconnectTracker();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('visibleModal')) {
            if (this.visibleModal === ZipInModalEnum.zipIn) {
                this.openSheet();
            }
            else {
                this.closeSheet();
            }
        }
        if (this.cardElement) {
            this.setCardProperties(this.cardElement);
        }
        if (changedProperties.has('state')) {
            this.recreateSheetFooter();
        }
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        if (changedProperties.has('analytics') && this.hasTranslationsAndSettings()) {
            this.disconnectTracker();
            this.connectTracker();
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation;
    }
    getSettings() {
        return this.dexfSettings;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        if (!translations) {
            throw new Error('Translations must be defined either through the "translations" or "localizedInformation" prop.');
        }
        return translations;
    }
    async connectTracker() {
        if (this.analytics) {
            const reporter = this.analytics.__INTERNAL__reporter;
            if (reporter?.connected === false) {
                // Should use an existing reporter, but wait for it to be connected
                return;
            }
            // Use existing connected reporter, or the tracker should create a new one.
            const reporterId = reporter ? reporter.id : undefined;
            this.tracker = new ZipInTracker({
                ...this.analytics,
                settings: this.getSettings(),
                reporterId
            });
            await this.tracker?.connect();
        }
    }
    disconnectTracker() {
        this.tracker?.disconnect();
        this.tracker = null;
    }
    closeSheet() {
        if (this.sheetElement) {
            this.sheetElement.open = false;
            this.sheetElement = null;
            this.cardElement = null;
            this.sheetFooterElement = null;
        }
    }
    openSheet() {
        if (this.sheetElement) {
            return;
        }
        this.requestUpdate();
        const sheetElement = this.createSheetElement();
        sheetElement.addEventListener('closed', () => {
            sheetElement.parentElement?.removeChild(sheetElement);
        });
        this.sheetElement = sheetElement;
        this.cardElement = sheetElement.querySelector(this.customElements.getInternalName('kompis-zip-in-card'));
        this.sheetFooterElement = sheetElement.querySelector(this.customElements.getInternalName('skapa-modal-footer'));
        const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
        parent.appendChild(sheetElement);
        sheetElement.open = true;
        this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: {
                visibleModal: ZipInModalEnum.zipIn
            }
        }));
    }
    createSheetElement() {
        const sheetElement = this.createSheet();
        const sheetHeaderElement = this.createSheetHeader();
        const zipInCard = this.createCard();
        const sheetFooterElement = this.createSheetFooter();
        sheetElement.appendChild(sheetHeaderElement);
        sheetElement.appendChild(zipInCard);
        sheetElement.appendChild(sheetFooterElement);
        return sheetElement;
    }
    createSheet() {
        const sheetElement = this.createInternalElement('skapa-sheet');
        sheetElement.setAttribute('aria-label', this.getTranslations().useLocationHeader);
        sheetElement.size = 'small';
        sheetElement.style.zIndex = '500';
        sheetElement.fullHeight = true;
        sheetElement.alignToStart = this.sheetAlignment === SheetAlignmentEnum.left;
        sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
        sheetElement.addEventListener('closerequest', () => {
            if (this.sheetElement) {
                this.dispatchEvent(new CustomEvent('modalClose', {
                    bubbles: true,
                    detail: {
                        visibleModal: null
                    }
                }));
            }
        });
        return sheetElement;
    }
    createSheetHeader() {
        const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
        sheetHeaderElement.slot = 'header';
        const headerSpan = document.createElement('span');
        headerSpan.slot = 'closebutton-label';
        headerSpan.innerText = this.getTranslations().modalAriaLabelButtonClose;
        sheetHeaderElement.appendChild(headerSpan);
        return sheetHeaderElement;
    }
    createCard() {
        const zipInCard = this.createInternalElement('kompis-zip-in-card');
        this.setCardProperties(zipInCard);
        zipInCard.addEventListener('save', this.onSave.bind(this));
        return zipInCard;
    }
    onSave() {
        if (!this.cardElement) {
            return;
        }
        const zipCode = this.cardElement.getZipCode();
        this.dispatchEvent(new CustomEvent('save', {
            bubbles: true,
            detail: {
                visibleModal: null,
                zipCode
            }
        }));
        this.sendZipCodeActionEnter();
    }
    setCardProperties(cardElement) {
        cardElement.theme = this.theme;
        cardElement.localizedInformation = this.localizedInformation;
        cardElement.translations = this.translations;
        cardElement.zipCode = this.zipCode;
        cardElement.state = this.state;
    }
    recreateSheetFooter() {
        if (!this.sheetElement) {
            return;
        }
        if (this.sheetFooterElement) {
            this.sheetElement.removeChild(this.sheetFooterElement);
            this.sheetFooterElement = null;
        }
        const footer = this.createSheetFooter();
        this.sheetElement.appendChild(footer);
        this.sheetFooterElement = footer;
    }
    createSheetFooter() {
        const sheetFooterElement = this.createInternalElement('skapa-modal-footer');
        sheetFooterElement.slot = 'footer';
        sheetFooterElement.stacked = true;
        const saveButton = this.getSaveButton();
        const forgetButton = this.getForgetButton();
        if (forgetButton) {
            sheetFooterElement.appendChild(forgetButton);
        }
        sheetFooterElement.appendChild(saveButton);
        return sheetFooterElement;
    }
    getSaveButton() {
        const button = this.createInternalElement('skapa-button');
        button.slot = 'primary-button';
        button.variant = 'primary';
        button.textContent = this.getTranslations().save;
        button.loading = this.state === ZipInCardStateEnum.loading;
        button.loadingSrText = this.getTranslations().loading;
        button.addEventListener('click', () => {
            this.onSave();
        });
        return button;
    }
    getForgetButton() {
        if (this.state !== ZipInCardStateEnum.submitted) {
            return null;
        }
        const forgetLinkTranslation = this.getTranslations().zipCodeForget;
        const button = this.createInternalElement('skapa-button');
        button.slot = 'secondary-button';
        button.variant = 'secondary';
        button.textContent = forgetLinkTranslation;
        button.addEventListener('click', () => {
            this.dispatchEvent(new CustomEvent('forget', {
                bubbles: true,
                detail: {
                    visibleModal: null,
                    state: ZipInCardStateEnum.default,
                    zipCode: ''
                }
            }));
            this.sendZipCodeActionRemove();
        });
        return button;
    }
    sendZipCodeActionEnter() {
        this.tracker?.sendZipCodeAction(ZipInActionEnum.save);
    }
    sendZipCodeActionRemove() {
        this.tracker?.sendZipCodeAction(ZipInActionEnum.forget);
    }
}
__decorate([
    property({ type: Object, required: true })
], ZipIn.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], ZipIn.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], ZipIn.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], ZipIn.prototype, "settings", void 0);
__decorate([
    property({ type: String })
], ZipIn.prototype, "visibleModal", void 0);
__decorate([
    property({ type: String })
], ZipIn.prototype, "zipCode", void 0);
__decorate([
    property({ type: String })
], ZipIn.prototype, "state", void 0);
__decorate([
    property({ type: String })
], ZipIn.prototype, "sheetAlignment", void 0);
__decorate([
    property({ type: Object })
], ZipIn.prototype, "analytics", void 0);
//# sourceMappingURL=ZipIn.js.map